import { useEffect, useMemo, useState, useCallback } from "react";
import {
  TrendingDown, TrendingUp, Wallet, AlertCircle, RefreshCw,
  ArrowDownCircle, ArrowUpCircle, Landmark,
} from "lucide-react";
import { FilterBar }           from "@/components/ui/FilterBar";
import { Card }                from "@/components/ui/Card";
import { Button }              from "@/components/ui/Button";
import { useModules, FinanceiroData } from "@/context/ModulesContext";
import { useInstallmentsMap } from "@/hooks/useInstallmentsMap";
import { usePaymentCategories }  from "@/hooks/usePaymentCategories";
import { useBankBalance }        from "@/hooks/useBankBalance";
import { useAppAccess }          from "@/hooks/useAppAccess";
import { formatCurrency } from "@/utils/format";
import { cn } from "@/utils/cn";
import { DespesasDetailModal }  from "./charts/DespesasDetailModal";
import { ReceitasDetailModal }  from "./charts/ReceitasDetailModal";
import { BankBalanceModal }     from "./charts/BankBalanceModal";
import {
  OutcomeItem,
  IncomeItem,
  BankStatementMovement,
  BankMovementFinanceiroLine,
  filterIncomeRecebidosNoPeriodo,
  incomeReceiptLiquido,
  outcomeParcelValorExibido,
} from "@/services/financeiro";

import {
  computeFluxoMensal,
  computeSaldoAcumulado,
  computePagamentosPorCentroDeCusto,
  computeRecebimentosPorCentroDeCusto,
  computeDespesasPorConta,
  computeRankingCredores,
} from "./chartUtils";

import { FluxoMensal } from "./charts/FluxoMensal";
import { RecebimentosPorCentro } from "./charts/RecebimentosPorCentro";
import { PagamentosPorCentro } from "./charts/PagamentosPorCentro";
import { DespesasPorConta } from "./charts/DespesasPorConta";
import { EvolucaoSaldo }    from "./charts/EvolucaoSaldo";
import { RankingCredores }  from "./charts/RankingCredores";
import { InadimplenciaPanel } from "./Inadimplencia";

/* ─── KPIs ───────────────────────────────────────────────────────────────── */
function inRange(date: string, startDate: string, endDate: string): boolean {
  const d = (date || "").substring(0, 10);
  if (d.length < 10) return false;
  return d >= startDate && d <= endDate;
}

function computeKpis(d: FinanceiroData, startDate: string, endDate: string) {
  const stm = (d.statements ?? []).filter((s) => inRange(s.movementDate, startDate, endDate));
  const extratoCredFallback = stm.reduce((s, x) => s + x.credit, 0);
  const extratoDebFallback  = stm.reduce((s, x) => s + x.debit, 0);
  const extratoCred = typeof d.recebimentosBankMovementOperacaoTotal === "number"
    ? d.recebimentosBankMovementOperacaoTotal
    : extratoCredFallback;
  const extratoDeb = typeof d.pagamentosBankMovementTotal === "number"
    ? d.pagamentosBankMovementTotal
    : extratoDebFallback;

  const incomeRecebido = d.income
    .filter((i) => i.isPaid && inRange(i.paymentDate, startDate, endDate))
    .reduce((s, i) => s + incomeReceiptLiquido(i), 0);
  /* Recebimentos: recebidos por data de pagamento + créditos do extrato no período aplicado. */
  const totalReceitas = incomeRecebido + extratoCred;

  /* Pagamentos: data de pagamento no período + débitos do extrato no período */
  const totalDespesas =
    d.outcome
      .filter((o) => o.isPaid && inRange(o.paymentDate, startDate, endDate))
      .reduce((s, o) => s + outcomeParcelValorExibido(o), 0) + extratoDeb;
  const saldoLiquido   = totalReceitas - totalDespesas;

  /* Contas em aberto por vencimento no período */
  const contasPagar = d.outcome
    .filter((o) => !o.isPaid && inRange(o.dueDate || o.issueDate, startDate, endDate))
    .reduce((s, o) => s + o.balanceAmount, 0);
  const contasReceber = d.income
    .filter((i) => !i.isPaid && inRange(i.dueDate || i.issueDate, startDate, endDate))
    .reduce((s, i) => s + i.balanceAmount, 0);

  return {
    totalDespesas,
    totalReceitas,
    saldoLiquido,
    contasPagar,
    contasReceber,
    extratoCred,
    extratoDeb,
    recebimentosCardFromBulk:
      typeof d.recebimentosBankMovementOperacaoTotal === "number" ||
      typeof d.recebimentosBankMovementTotal === "number",
  };
}

/* ─── Página ─────────────────────────────────────────────────────────────── */
type FinanceiroTab = "geral" | "inadimplencia";

export function FinanceiroPage() {
  const { financeiro, ccOptions, loadingCC, ccNameMap } = useModules();
  const { values, setValues, apply, reset, autoLoad, applied, data, loading, error } = financeiro;
  const [tab, setTab] = useState<FinanceiroTab>("geral");
  const [inadRefresh, setInadRefresh] = useState(0);

  /* Carrega dados na primeira visita; nas seguintes, usa o cache do contexto. */
  useEffect(() => { autoLoad(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Modal de detalhes de despesas (outcome) ── */
  const [detailModal, setDetailModal] = useState<{
    title: string;
    items: OutcomeItem[];
    statementDebits?: BankStatementMovement[];
    bankMovementPagamentos?: BankMovementFinanceiroLine[];
  } | null>(null);

  /* ── Modal de detalhes de receitas (income) ── */
  const [receitasModal, setReceitasModal] = useState<{
    title: string;
    items: IncomeItem[];
    statementCredits?: BankStatementMovement[];
    totalIgualAoCardRecebimentos?: number;
    extratoCreditoNoKpi?: number;
    bankMovementRecebimentos?: BankMovementFinanceiroLine[];
  } | null>(null);

  const installmentsMap = useInstallmentsMap();
  const paymentCatMap    = usePaymentCategories();
  const { isAdmin }      = useAppAccess();
  const bankBalance      = useBankBalance(isAdmin);
  const [bankBalanceOpen, setBankBalanceOpen] = useState(false);

  const openModal = useCallback(
    (
      title: string,
      items: OutcomeItem[],
      statementDebits?: BankStatementMovement[],
      bankMovementPagamentos?: BankMovementFinanceiroLine[],
    ) => {
      setDetailModal({ title, items, statementDebits, bankMovementPagamentos });
    },
    [],
  );

  const openReceitasModal = useCallback(
    (
      title: string,
      items: IncomeItem[],
      statementCredits?: BankStatementMovement[],
      totalIgualAoCardRecebimentos?: number,
      extratoCreditoNoKpi?: number,
      bankMovementRecebimentos?: BankMovementFinanceiroLine[],
    ) => {
      setReceitasModal({
        title,
        items,
        statementCredits,
        totalIgualAoCardRecebimentos,
        extratoCreditoNoKpi,
        bankMovementRecebimentos,
      });
    },
    [],
  );

  const pagamentosModalItems = useMemo(
    () => (data
      ? data.outcome.filter((o) => o.isPaid && inRange(o.paymentDate, applied.startDate, applied.endDate))
      : []),
    [data, applied.startDate, applied.endDate],
  );
  const contasPagarModalItems = useMemo(
    () => (data
      ? data.outcome.filter((o) => !o.isPaid && inRange(o.dueDate || o.issueDate, applied.startDate, applied.endDate))
      : []),
    [data, applied.startDate, applied.endDate],
  );
  const contasReceberModalItems = useMemo(
    () => (data
      ? data.income.filter((i) => !i.isPaid && inRange(i.dueDate || i.issueDate, applied.startDate, applied.endDate))
      : []),
    [data, applied.startDate, applied.endDate],
  );
  const statementDebitsInRange = useMemo(
    () => (data
      ? (data.statements ?? []).filter((s) => s.debit > 0 && inRange(s.movementDate, applied.startDate, applied.endDate))
      : []),
    [data, applied.startDate, applied.endDate],
  );
  const statementCreditsInRange = useMemo(
    () => (data
      ? (data.statements ?? []).filter((s) => s.credit > 0 && inRange(s.movementDate, applied.startDate, applied.endDate))
      : []),
    [data, applied.startDate, applied.endDate],
  );

  const kpis        = data ? computeKpis(data, applied.startDate, applied.endDate) : null;
  const isFirstLoad = loading && !data;

  const fluxoMensal = useMemo(
    () => (data ? computeFluxoMensal(data.outcome, data.income, data.statements ?? []) : []),
    [data],
  );
  const saldoAcum   = useMemo(() => computeSaldoAcumulado(fluxoMensal),                        [fluxoMensal]);
  const pagamentosPorCc = useMemo(
    () =>
      data
        ? computePagamentosPorCentroDeCusto(
            data.outcome,
            applied.startDate,
            applied.endDate,
            ccNameMap,
          )
        : [],
    [data, applied.startDate, applied.endDate, ccNameMap],
  );
  const recebimentosPorCc = useMemo(
    () =>
      data
        ? computeRecebimentosPorCentroDeCusto(
            data.income,
            applied.startDate,
            applied.endDate,
            ccNameMap,
          )
        : [],
    [data, applied.startDate, applied.endDate, ccNameMap],
  );
  const porConta    = useMemo(() => data ? computeDespesasPorConta(data.outcome) : [],         [data]);
  const credores    = useMemo(() => data ? computeRankingCredores(data.outcome) : [],          [data]);

  const recebimentosModalItems = useMemo(
    () => (data ? filterIncomeRecebidosNoPeriodo(data.income, applied.startDate, applied.endDate) : []),
    [data, applied.startDate, applied.endDate],
  );

  const hasCharts = data || loading;

  return (
    <div className="flex flex-col gap-6">

      {/* ── Cabeçalho ── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Financeiro</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {tab === "inadimplencia"
              ? "Boletos em atraso, aging e régua de cobrança no período que você escolher"
              : "Visão estratégica de receitas, despesas e fluxo de caixa no período"}
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => (tab === "geral" ? apply() : setInadRefresh((n) => n + 1))}
          loading={tab === "geral" ? loading : undefined}
          leftIcon={<RefreshCw className="w-4 h-4" />}
        >
          Atualizar
        </Button>
      </div>

      <nav className="-mb-2 flex gap-6 border-b border-slate-200" aria-label="Abas do Financeiro">
        {([
          { id: "geral" as const, label: "Visão geral" },
          { id: "inadimplencia" as const, label: "Inadimplência" },
        ]).map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={cn(
              "py-3 text-sm font-medium border-b-2 transition-colors -mb-px",
              tab === item.id
                ? "border-brand-600 text-brand-700"
                : "border-transparent text-slate-500 hover:text-slate-700",
            )}
            aria-selected={tab === item.id}
            role="tab"
          >
            {item.label}
          </button>
        ))}
      </nav>

      {tab === "inadimplencia" ? (
        <InadimplenciaPanel refreshToken={inadRefresh} />
      ) : (
      <div className="flex flex-col gap-6">

      {/* ── Filtros ── */}
      <FilterBar
        values={values} onChange={setValues} onApply={apply} onReset={reset}
        costCenterOptions={ccOptions} loadingOptions={loadingCC} loading={loading}
      />

      {/* ── Erro ── */}
      {error && (
        <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm font-medium">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={apply} className="ml-auto text-red-500 hover:text-red-700 underline text-xs">
            Tentar novamente
          </button>
        </div>
      )}

      {/* ── KPI Cards ── */}
      <div className={cn(
        "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4",
        isAdmin ? "2xl:grid-cols-6" : "2xl:grid-cols-5",
      )}>
        <Card
          title="Pagamentos"
          value={isFirstLoad ? undefined : formatCurrency(kpis?.totalDespesas ?? 0)}
          subtitle={
            !isFirstLoad &&
            typeof data?.pagamentosBankMovementTotal === "number" &&
            (kpis?.extratoDeb ?? 0) > 0
              ? "Parcelas quitadas e movimento bancário no período"
              : "Parcelas com status Pago no período"
          }
          icon={<TrendingDown className="w-5 h-5" />}
          loading={isFirstLoad}
          className="border-l-4 border-l-rose-400"
          onClick={data
            ? () =>
                setDetailModal({
                  title: "Pagamentos — parcelas pagas + extrato (débitos)",
                  items: pagamentosModalItems,
                  statementDebits: statementDebitsInRange,
                  bankMovementPagamentos: data.bankMovementPagamentoLines,
                })
            : undefined}
        />
        <Card
          title="Contas a Pagar"
          value={isFirstLoad ? undefined : formatCurrency(kpis?.contasPagar ?? 0)}
          icon={<ArrowDownCircle className="w-5 h-5" />}
          loading={isFirstLoad}
          className="border-l-4 border-l-orange-400"
          onClick={data ? () => openModal("Contas a Pagar — Parcelas em Aberto", contasPagarModalItems) : undefined}
        />
        <Card
          title="Recebimentos"
          value={isFirstLoad ? undefined : formatCurrency(kpis?.totalReceitas ?? 0)}
          subtitle={
            !isFirstLoad && kpis?.recebimentosCardFromBulk
              ? "Total integrado ao movimento bancário no período"
              : !isFirstLoad && (kpis?.extratoCred ?? 0) > 0
              ? "Títulos recebidos e movimento bancário no período"
              : "Títulos com status Recebido no período"
          }
          icon={<TrendingUp className="w-5 h-5" />}
          loading={isFirstLoad}
          className="border-l-4 border-l-emerald-400"
          onClick={
            data && kpis
              ? () =>
                  openReceitasModal(
                    "Recebimentos — títulos recebidos + extrato (créditos)",
                    recebimentosModalItems,
                    statementCreditsInRange,
                    kpis.totalReceitas,
                    kpis.extratoCred,
                    data.bankMovementRecebimentoLines,
                  )
              : undefined
          }
        />
        <Card
          title="Contas a Receber"
          value={isFirstLoad ? undefined : formatCurrency(kpis?.contasReceber ?? 0)}
          icon={<ArrowUpCircle className="w-5 h-5" />}
          loading={isFirstLoad}
          className="border-l-4 border-l-teal-400"
          onClick={data ? () => openReceitasModal("Contas a Receber — Títulos em Aberto", contasReceberModalItems) : undefined}
        />
        <Card
          title="Superávit/Déficit"
          value={isFirstLoad ? undefined : formatCurrency(kpis?.saldoLiquido ?? 0)}
          icon={<Wallet className="w-5 h-5" />}
          loading={isFirstLoad}
          className={
            !isFirstLoad && (kpis?.saldoLiquido ?? 0) < 0
              ? "border-l-4 border-l-red-500"
              : "border-l-4 border-l-blue-400"
          }
        />
        {isAdmin && (
          <Card
            title="Saldo Bancário"
            value={bankBalance.loading ? undefined : formatCurrency(bankBalance.total)}
            subtitle={bankBalance.balanceDate ? `Posição: ${bankBalance.balanceDate.split("-").reverse().join("/")}` : undefined}
            icon={<Landmark className="w-5 h-5" />}
            loading={bankBalance.loading}
            className={
              !bankBalance.loading && bankBalance.total < 0
                ? "border-l-4 border-l-red-500"
                : "border-l-4 border-l-violet-400"
            }
            onClick={!bankBalance.loading && bankBalance.accounts.length > 0
              ? () => setBankBalanceOpen(true)
              : undefined}
          />
        )}
      </div>

      {(isFirstLoad || (loadingCC && !data)) && (
        <div className="text-center py-4">
          <p className="text-sm text-slate-500">
            {loadingCC && !data
              ? "Carregando centros de custo…"
              : "Consultando o Sienge… isso pode levar alguns segundos."}
          </p>
        </div>
      )}

      {/* ══════════════════ SEÇÃO: FLUXO DE CAIXA ══════════════════ */}
      {hasCharts && (
        <>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2">
              Análise de Fluxo de Caixa
            </span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Linha 1: Recebimentos + Pagamentos por CC (50/50) */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:items-stretch [&>*]:min-h-0 lg:[&>*]:h-full">
            <RecebimentosPorCentro data={recebimentosPorCc} loading={isFirstLoad} />
            <PagamentosPorCentro data={pagamentosPorCc} loading={isFirstLoad} />
          </div>

          {/* Linha 2: Fluxo mensal em largura total (mesma altura do chart) */}
          <div className="w-full">
            <FluxoMensal data={fluxoMensal} loading={isFirstLoad} />
          </div>

          {/* ══════════════════ SEÇÃO: ANÁLISE DE DESPESAS ══════════════════ */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2">
              Análise de Despesas
            </span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          {/* Linha 2: Plano Financeiro + Ranking Credores */}
          <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
            <DespesasPorConta data={porConta} loading={isFirstLoad} />
            <RankingCredores data={credores} loading={isFirstLoad} />
          </div>

          {/* Evolução do Saldo — ao final da parte Financeiro */}
          <EvolucaoSaldo data={saldoAcum} loading={isFirstLoad} />
        </>
      )}
      {/* ── Modal detalhado de despesas ── */}
      {detailModal && (
        <DespesasDetailModal
          open={!!detailModal}
          onClose={() => setDetailModal(null)}
          title={detailModal.title}
          items={detailModal.items}
          statementDebits={detailModal.statementDebits}
          bankMovementPagamentos={detailModal.bankMovementPagamentos}
          installmentsMap={installmentsMap}
          ccNameMap={ccNameMap}
          paymentCatMap={paymentCatMap}
        />
      )}

      {/* ── Modal detalhado de receitas ── */}
      {receitasModal && (
        <ReceitasDetailModal
          open={!!receitasModal}
          onClose={() => setReceitasModal(null)}
          title={receitasModal.title}
          items={receitasModal.items}
          statementCredits={receitasModal.statementCredits}
          totalIgualAoCardRecebimentos={receitasModal.totalIgualAoCardRecebimentos}
          extratoCreditoNoKpi={receitasModal.extratoCreditoNoKpi}
          bankMovementRecebimentos={receitasModal.bankMovementRecebimentos}
          ccNameMap={ccNameMap}
          paymentCatMap={paymentCatMap}
        />
      )}

      {/* ── Modal saldo bancário (somente Administrador) ── */}
      {isAdmin && (
        <BankBalanceModal
          open={bankBalanceOpen}
          onClose={() => setBankBalanceOpen(false)}
          total={bankBalance.total}
          balanceDate={bankBalance.balanceDate}
          accounts={bankBalance.accounts}
        />
      )}
      </div>
      )}
    </div>
  );
}
